import { createApp } from 'vue';
import TMessageDialogVue from './TMessageDialogVue.vue';
import { registerPlugins } from '@/plugins';

interface DialogProps {
  type: 'info' | 'success' | 'alert' | 'error' | 'question';
  question?: boolean;
  extended?: boolean;
  message?: string;
  title?: string;
  okLabel?: string;
  cancelLabel?: string;
  okCallback?: () => void;
  cancelCallback?: () => void;
  escEnabled?: boolean;
  focusType?: 'none' | 'ok' | 'cancel';
  elementFocus?: HTMLElement | null;
  elementFocusOK?: HTMLElement | null;
  elementFocusCancel?: HTMLElement | null;
  showCloseButton?: boolean;
}

class Dialog {
  constructor(
    propType: DialogProps['type'],
    propQuestion: boolean = false,
    propExtended: boolean = false,
    propMessage: string = '',
    propTitle: string = '',
    propOkLabel: string = '',
    propCancelLabel: string = '',
    propOkCallback: () => void = () => {},
    propCancelCallback: () => void = () => {},
    propEscEnabled: boolean = true,
    propFocusType: 'none' | 'ok' | 'cancel' = 'none',
    propElementFocus: HTMLElement | null = null,
    propElementFocusOK: HTMLElement | null = null,
    propElementFocusCancel: HTMLElement | null = null,
    propShowCloseButton: boolean = false,
  ) {
    this.propType = propType;
    this.propQuestion = propQuestion;
    this.propExtended = propExtended;
    this.propMessage = propMessage;
    this.propTitle = propTitle;
    this.propOkLabel = propOkLabel;
    this.propCancelLabel = propCancelLabel;
    this.propOkCallback = propOkCallback;
    this.propCancelCallback = propCancelCallback;
    this.propEscEnabled = propEscEnabled;
    this.propFocusType = propFocusType;
    this.propElementFocus = propElementFocus;
    this.propElementFocusOK = propElementFocusOK;
    this.propElementFocusCancel = propElementFocusCancel;
    this.propShowCloseButton = propShowCloseButton;
  }

  private propType: DialogProps['type'];
  private propQuestion: DialogProps['question'];
  private propExtended: DialogProps['extended'];
  private propMessage: DialogProps['message'];
  private propTitle: DialogProps['title'];
  private propOkLabel: DialogProps['okLabel'];
  private propCancelLabel: DialogProps['cancelLabel'];
  private propOkCallback: DialogProps['okCallback'];
  private propCancelCallback: DialogProps['cancelCallback'];
  private propEscEnabled: DialogProps['escEnabled'];
  private propFocusType: DialogProps['focusType'];
  private propElementFocus: DialogProps['elementFocus'];
  private propElementFocusOK: DialogProps['elementFocusOK'];
  private propElementFocusCancel: DialogProps['elementFocusCancel'];
  private propShowCloseButton: DialogProps['showCloseButton'];

  /**
   * Define o título a ser exibido na caixa de diálogo.
   * @default ""
   */
  title(value: DialogProps['title']) {
    this.propTitle = value; // Atribui o valor ao propTitle
    return this; // Retorna a própria instância da classe
  }

  /**
   * Define a mensagem a ser exibida na caixa de diálogo.
   * @default ""
   */
  message(value: DialogProps['message']) {
    this.propMessage = value;
    return this;
  }

  /**
   * Define se a caixa de diálogo será exibida com o botão de cancelar. type="question" já vem com essa opção habilitada por padrão.
   * @default false
   */
  question(value: DialogProps['question'] = true) {
    this.propQuestion = value;
    return this;
  }

  /**
   * Define se a caixa de diálogo será exibida na forma expandida.
   * @default false
   */
  extended(value: DialogProps['extended'] = true) {
    this.propExtended = value;
    return this;
  }

  /**
   * Define o texto do botão de confirmação.
   * @default "OK"
   */
  okLabel(value: DialogProps['okLabel']) {
    this.propOkLabel = value;
    return this;
  }

  /**
   * Define o texto do botão de cancelamento.
   * @default "Cancelar"
   */
  cancelLabel(value: DialogProps['cancelLabel']) {
    this.propCancelLabel = value;
    return this;
  }

  /**
   * Define a função a ser executada ao clicar no botão de confirmação.
   * @default () => {}
   */
  okCallback(value: DialogProps['okCallback']) {
    this.propOkCallback = value;
    return this;
  }

  /**
   * Define a função a ser executada ao clicar no botão de cancelamento.
   * @default () => {}
   */
  cancelCallback(value: DialogProps['cancelCallback']) {
    this.propCancelCallback = value;
    return this;
  }

  /**
   * Define se a tecla ESC será habilitada para fechar a caixa de diálogo.
   * @default true
   */
  escEnabled(value: DialogProps['escEnabled'] = true) {
    this.propEscEnabled = value;
    return this;
  }

  /**
   * Define o tipo de foco a ser aplicado ao abrir a caixa de diálogo.
   * @values
   * - `"none"`: Nenhum foco será aplicado.
   * - `"ok"`: O foco será aplicado no botão de confirmação.
   * - `"cancel"`: O foco será aplicado no botão de cancelamento.
   * @default "none"
   */
  focusType(value: DialogProps['focusType']) {
    this.propFocusType = value;
    return this;
  }

  /**
   * Define o elemento a ser focado quando fechar a caixa de diálogo.
   * @default null
   */
  elementFocus(value: DialogProps['elementFocus']) {
    this.propElementFocus = value;
    return this;
  }

  /**
   * Define o elemento a ser focado quando botão de confirmação for clicado.
   * @default null
   */
  elementFocusOK(value: DialogProps['elementFocusOK']) {
    this.propElementFocusOK = value;
    return this;
  }

  /**
   * Define o elemento a ser focado quando botão de cancelamento for clicado.
   * @default null
   */
  elementFocusCancel(value: DialogProps['elementFocusCancel']) {
    this.propElementFocusCancel = value;
    return this;
  }

  /**
   * Define se o botão de fechar será exibido.
   * @default false
   */
  closeButton(value: DialogProps['showCloseButton'] = true) {
    this.propShowCloseButton = value;
    return this;
  }

  /**
   * Exibe a caixa de diálogo.
   *
   */
  show() {
    const appContainer = document.createElement('div');
    document.body.appendChild(appContainer);

    const app = createApp(TMessageDialogVue, {
      type: this.propType,
      question: this.propQuestion,
      extended: this.propExtended,
      message: this.propMessage,
      title: this.propTitle,
      okLabel: this.propOkLabel,
      cancelLabel: this.propCancelLabel,
      okCallback: this.propOkCallback,
      cancelCallback: this.propCancelCallback,
      escEnabled: this.propEscEnabled,
      focusType: this.propFocusType,
      elementFocus: this.propElementFocus,
      elementFocusOK: this.propElementFocusOK,
      elementFocusCancel: this.propElementFocusCancel,
      showCloseButton: this.propShowCloseButton,
    });
    registerPlugins(app);

    const dialogComponent = app.mount(appContainer) as InstanceType<typeof TMessageDialogVue>;

    dialogComponent.$el.addEventListener('afterClose', () => {
      document.body.removeChild(appContainer);
    });
  }
}

export default class TMessageDialog {
  /**
   * Caixa de diálogo na cor azul com ícone de informação.
   * @example
   * ```typescript
   * TMessageDialog.info()
   *  .title("Título da caixa de diálogo")
   * .message("Mensagem da caixa de diálogo")
   * .show();
   * ```
   */
  static info() {
    return new Dialog('info');
  }

  /**
   * Caixa de diálogo na cor verde com ícone de sucesso.
   * @example
   * ```typescript
   * TMessageDialog.success()
   * .title("Título da caixa de diálogo")
   * .message("Mensagem da caixa de diálogo")
   * .show();
   * ```
   */
  static success() {
    return new Dialog('success');
  }

  /**
   * Caixa de diálogo na cor amarela com ícone de alerta.
   * @example
   * ```typescript
   * TMessageDialog.alert()
   * .title("Título da caixa de diálogo")
   * .message("Mensagem da caixa de diálogo")
   * .show();
   * ```
   */
  static alert() {
    return new Dialog('alert');
  }

  /**
   * Caixa de diálogo na cor vermelha com ícone de erro.
   * @example
   * ```typescript
   * TMessageDialog.error()
   * .title("Título da caixa de diálogo")
   * .message("Mensagem da caixa de diálogo")
   * .show();
   * ```
   * */
  static error() {
    return new Dialog('error');
  }

  /**
   * Caixa de diálogo na cor cinza com ícone de interrogação.
   * @example
   * ```typescript
   * TMessageDialog.question()
   * .title("Título da caixa de diálogo")
   * .message("Mensagem da caixa de diálogo")
   * .show();
   * ```
   */
  static question() {
    return new Dialog('question');
  }
}
